import { Injectable } from '@angular/core';
import { map, withLatestFrom } from 'rxjs';
import { HeatloadStore } from '../../state/heatload/heatload.store';
import { BuildingType, CountryCode, FormName, HeatLoadCalculationMethod, SuccessMessages } from '@nx-customer-apps/shared/enums';
import { FormOptions, LivingAreaFormValue } from '@nx-customer-apps/shared/interfaces';
import { ProjectStore } from '../../state/project/project.store';
import { TranslateService } from '@ngx-translate/core';
import { HeatloadFormOptionsService } from './heatload-form-options.service';
import { HeatloadPrefillMapper } from '../../services';
import { AccordionService } from '../../shared/interfaces';
import { LivingAreaFormService } from '../../shared/components/living-area';

@Injectable({
    providedIn: 'root'
})
export class LivingAreaService implements AccordionService {
    constructor(
        private heatloadFormOptionsService: HeatloadFormOptionsService,
        private prefillMapper: HeatloadPrefillMapper,
        private heatloadStore: HeatloadStore,
        private translate: TranslateService,
        private projectStore: ProjectStore,
        private formService: LivingAreaFormService
    ) {}

    public questions$ = this.heatloadStore.calculationMethod$.pipe(
        withLatestFrom(this.projectStore.project$, this.heatloadStore.cache$, (method, project, cache) => ({
            isLivingArea: method?.value === HeatLoadCalculationMethod.LivingSpace,
            project,
            cache
        })),
        map(state => {
            if (!state.isLivingArea) {
                return null;
            }
            const projectPrefill = this.prefillMapper.projectToLivingAreaPrefill(state.project!);
            const cachePrefill = this.prefillMapper.stateToLivingAreaPrefill(state.cache);
            const options: FormOptions = this.heatloadFormOptionsService.getLivingAreaFormOptions({
                prefill: cachePrefill || projectPrefill,
                buildingType: state.project!.building.buildingType as BuildingType,
                countryCode: state.project!.address.countryCode as CountryCode
            });
            return this.formService.getLivingAreaQuestions(options as FormOptions);
        })
    );

    public getSuccessMessage(formValue: LivingAreaFormValue): string {
        return this.translate.instant(SuccessMessages.LivingAreaSize, { value: formValue.livingArea });
    }

    public isExpanded$ = this.isSubmitedCalculationMethodSameValue$();

    private isSubmitedCalculationMethodSameValue$() {
        return this.heatloadStore.sameValueSubmited$.pipe(
            map(
                form =>
                    form?.formName === FormName.CalculationMethod &&
                    form.value.methods.standardMethod === HeatLoadCalculationMethod.LivingSpace
            )
        );
    }
}
